import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

const TURNSTILE_SCRIPT_ID = 'cf-turnstile-script';
const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

const ensureTurnstileScript = () => {
  if (typeof window === 'undefined') return null;

  const existing = document.getElementById(TURNSTILE_SCRIPT_ID);
  if (existing) return existing;

  const script = document.createElement('script');
  script.id = TURNSTILE_SCRIPT_ID;
  script.src = TURNSTILE_SCRIPT_SRC;
  script.async = true;
  script.defer = true;
  document.head.appendChild(script);
  return script;
};

export default function TurnstileWidget({ siteKey, action, onTokenChange, resetKey }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onTokenChangeRef = useRef(onTokenChange);

  useEffect(() => {
    onTokenChangeRef.current = onTokenChange;
  }, [onTokenChange]);

  useEffect(() => {
    if (!siteKey || !containerRef.current || typeof window === 'undefined') {
      return undefined;
    }

    const script = ensureTurnstileScript();
    let cancelled = false;

    const renderWidget = () => {
      if (cancelled || !window.turnstile || !containerRef.current) {
        return;
      }

      if (widgetIdRef.current !== null) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // Ignore stale widget IDs.
        }
        widgetIdRef.current = null;
      }

      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        action,
        theme: 'light',
        callback: (token) => onTokenChangeRef.current(token),
        'expired-callback': () => onTokenChangeRef.current(''),
        'error-callback': () => onTokenChangeRef.current(''),
      });
    };

    if (window.turnstile) {
      renderWidget();
    } else if (script) {
      script.addEventListener('load', renderWidget, { once: true });
    }

    return () => {
      cancelled = true;
      if (script) {
        script.removeEventListener('load', renderWidget);
      }
      if (window.turnstile && widgetIdRef.current !== null) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // Ignore if widget is already gone.
        }
      }
      widgetIdRef.current = null;
    };
  }, [action, siteKey]);

  useEffect(() => {
    if (!window.turnstile || widgetIdRef.current === null) {
      return;
    }

    try {
      window.turnstile.reset(widgetIdRef.current);
      onTokenChangeRef.current('');
    } catch {
      onTokenChangeRef.current('');
    }
  }, [resetKey]);

  if (!siteKey) {
    return null;
  }

  return <div ref={containerRef} className="auth-card__turnstile" />;
}

TurnstileWidget.propTypes = {
  siteKey: PropTypes.string,
  action: PropTypes.string,
  onTokenChange: PropTypes.func,
  resetKey: PropTypes.number,
};

TurnstileWidget.defaultProps = {
  siteKey: '',
  action: 'submit',
  onTokenChange: () => {},
  resetKey: 0,
};
